import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function EmployeeCodeAssignments({ companyId, employeeId, canEdit }) {
  const canDelete = !useClientPerms().isClient;
  const [earnCodes, setEarnCodes] = useState([]);
  const [dedCodes, setDedCodes] = useState([]);
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: "earning", code_id: "", amount: "", effective_from: "" });

  const load = useCallback(() => {
    if (!employeeId) return;
    api.get(`/employees/${employeeId}/code-assignments`).then((r) => setRows(r.data.assignments || [])).catch(() => setRows([]));
  }, [employeeId]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/earning-codes`).then((r) => setEarnCodes((r.data.codes || []).filter((c) => c.active))).catch(() => {});
    api.get(`/companies/${companyId}/deduction-codes`).then((r) => setDedCodes((r.data.codes || []).filter((c) => c.active))).catch(() => {});
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const codeList = form.kind === "earning" ? earnCodes : dedCodes;
  const nameFor = (kind, id) => {
    const list = kind === "earning" ? earnCodes : dedCodes;
    const c = list.find((x) => x.code_id === id);
    return c ? `${c.code} — ${c.name}` : id;
  };

  const add = async () => {
    if (!form.code_id) { toast.error("Choose a code"); return; }
    try {
      await api.post(`/employees/${employeeId}/code-assignments`, {
        kind: form.kind, code_id: form.code_id,
        amount: form.amount === "" ? null : Number(form.amount),
        effective_from: form.effective_from || null, active: true });
      toast.success("Assignment added");
      setAdding(false); setForm({ kind: "earning", code_id: "", amount: "", effective_from: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (a) => {
    try { await api.delete(`/employees/${employeeId}/code-assignments/${a.assignment_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="py-2 space-y-3" data-testid="employee-code-assignments">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned earning & deduction codes</div>
        {canEdit && <button data-testid="assign-add" onClick={() => setAdding((s) => !s)} className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-xs hover:bg-black hover:text-white transition-colors"><Plus className="h-3.5 w-3.5" /> Assign code</button>}
      </div>

      {adding && (
        <div className="border border-border bg-neutral-50 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end" data-testid="assign-form">
          <label className="text-xs">Kind
            <select data-testid="assign-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, code_id: "" }))} className="mt-1 w-full border border-input bg-white px-2 py-1.5 text-sm">
              <option value="earning">Earning</option><option value="deduction">Deduction</option>
            </select>
          </label>
          <label className="text-xs col-span-1">Code
            <select data-testid="assign-code" value={form.code_id} onChange={(e) => setForm((f) => ({ ...f, code_id: e.target.value }))} className="mt-1 w-full border border-input bg-white px-2 py-1.5 text-sm">
              <option value="">Select…</option>
              {codeList.map((c) => <option key={c.code_id} value={c.code_id}>{c.code} — {c.name}</option>)}
            </select>
          </label>
          <label className="text-xs">Default amount
            <input data-testid="assign-amount" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1 w-full border border-input bg-white px-2 py-1.5 text-sm" />
          </label>
          <div className="flex gap-2">
            <input data-testid="assign-from" type="date" value={form.effective_from} onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} className="border border-input bg-white px-2 py-1.5 text-sm" />
            <button data-testid="assign-save" onClick={add} className="bg-black text-white px-3 py-1.5 text-xs">Add</button>
          </div>
        </div>
      )}

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="text-left"><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Code</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Effective from</th><th className="px-3 py-2 text-center">Active</th>{canEdit && <th className="px-3 py-2"></th>}</tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.assignment_id} data-testid={`assign-row-${a.assignment_id}`} className="border-t border-border/60">
                <td className="px-3 py-2 text-xs uppercase">{a.kind}</td>
                <td className="px-3 py-2">{nameFor(a.kind, a.code_id)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.amount != null ? a.amount : "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{a.effective_from || "—"}</td>
                <td className="px-3 py-2 text-center">{a.active ? "Yes" : "No"}</td>
                {canEdit && <td className="px-3 py-2 text-right">{canDelete && <button data-testid={`assign-remove-${a.assignment_id}`} onClick={() => remove(a)} className="text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button>}</td>}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="px-3 py-6 text-center text-muted-foreground text-xs">No codes assigned. System treatment is inherited from the code — nothing drives calculation yet (Phase 5.1).</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
